import Chart from 'chart.js';
import { TFunction } from 'i18next';
import {
	ALPHA_COLOR_PARAMS,
	GREEN_COLOR_STRING,
	RED_COLOR_STRING,
	SATISFACTION_LEVELS,
	YELLOW_COLOR_STRING,
} from '../../utils';
import { IChartAreaData, IXAxesMinMax } from '../../models';
import {
	AREA_LABEL_Y_OFFSET,
	AREAS_COUNT,
	CHART_TOP_PADDING,
	LINE_DASH,
	WORD_HALF_HEIGHT,
} from './chartConstants';

interface IAreaParameters {
	areaHeight: number;
	poorAreaWidth: number;
	okAreaWidth: number;
	goodAreaWidth: number;
}

enum InfluenceLabels {
	lower = 'lower',
	higher = 'higher',
}

enum SatisfactionLabels {
	poor = 'poor',
	ok = 'ok',
	good = 'good',
}

interface ISatisfactionLabels extends Record<SatisfactionLabels, string> {}
interface IInfluenceLabels extends Record<InfluenceLabels, string> {}
interface IChartLabels extends ISatisfactionLabels, IInfluenceLabels {}

const MIN_OK = SATISFACTION_LEVELS.ok.min;
const MIN_GOOD = SATISFACTION_LEVELS.good.min;

export const drawLine = (
	ctx: CanvasRenderingContext2D,
	xStart: number,
	xEnd: number,
	y: number,
	lineDash: number[],
) => {
	ctx.strokeStyle = 'gray';
	ctx.beginPath();
	ctx.setLineDash(lineDash);
	ctx.moveTo(xStart, y);
	ctx.lineTo(xEnd, y);
	ctx.stroke();
	ctx.setLineDash([]);
};

const getAreasParameters = (
	chartArea: Chart.ChartArea,
	minAndMaxTicksValues: IXAxesMinMax,
): IAreaParameters => {
	const { min, max } = minAndMaxTicksValues;
	const chartWidthPx = chartArea.right - chartArea.left;
	const chartHeightPx = chartArea.bottom - chartArea.top;
	const chartXRange = max - min;
	const areaHeight = chartHeightPx / AREAS_COUNT;

	const poorAreaWidth = ((MIN_OK - min) / chartXRange) * chartWidthPx;
	const okAreaWidth = ((MIN_GOOD - MIN_OK) / chartXRange) * chartWidthPx;
	const goodAreaWidth = ((max - MIN_GOOD) / chartXRange) * chartWidthPx;

	return {
		areaHeight,
		poorAreaWidth,
		okAreaWidth,
		goodAreaWidth,
	};
};

const carryAreasParams = (chartYStart: number, areaHeight: number) => {
	return (color: string, chartXStart: number, width: number) =>
		ALPHA_COLOR_PARAMS.reduce((res, item, idx) => {
			return res.concat({
				color: `rgba(${color}, ${item})`,
				x: chartXStart,
				y: chartYStart + areaHeight * idx,
				width,
				height: areaHeight,
			});
		}, [] as IChartAreaData[]);
};

const getChartAreasData = (
	chartArea: Chart.ChartArea,
	areaHeight: number,
	poorAreaWidth: number,
	okAreaWidth: number,
	goodAreaWidth: number,
) => {
	const getAreaData = carryAreasParams(chartArea.top, areaHeight);
	const redAreas = getAreaData(RED_COLOR_STRING, chartArea.left, poorAreaWidth);
	const yellowAreas = getAreaData(
		YELLOW_COLOR_STRING,
		chartArea.left + poorAreaWidth,
		okAreaWidth,
	);
	const greenAreas = getAreaData(
		GREEN_COLOR_STRING,
		chartArea.left + poorAreaWidth + okAreaWidth,
		goodAreaWidth,
	);

	return [...redAreas, ...yellowAreas, ...greenAreas];
};

const fillChartAreas = (
	chartInstance: Chart,
	areasParameters: IAreaParameters,
	isBlank: boolean,
) => {
	const { ctx, chartArea } = chartInstance;
	if (!ctx) return;
	const { left, right, top } = chartArea;

	const {
		areaHeight,
		poorAreaWidth,
		okAreaWidth,
		goodAreaWidth,
	} = areasParameters;

	// Get poor, ok, good areas coordinates/color to fill the satisfaction areas
	const areasToFill = getChartAreasData(
		chartArea,
		areaHeight,
		poorAreaWidth,
		okAreaWidth,
		goodAreaWidth,
	);
	const totalWidth = poorAreaWidth + okAreaWidth + goodAreaWidth;
	areasToFill.forEach(({ color, x, y, width, height }) => {
		ctx.fillStyle = color; // !!
		ctx.fillRect(x, y, width, height);
	});

	// Draw dashed lines to separate the areas
	drawLine(ctx, left, right, areaHeight + CHART_TOP_PADDING, LINE_DASH);
	drawLine(ctx, left, right, 2 * areaHeight + CHART_TOP_PADDING, LINE_DASH);

	if (isBlank) {
		ctx.fillStyle = `rgba(255, 255, 255, 0.46)`;
		ctx.fillRect(left, top, totalWidth, areaHeight);
	}
};

const getChartLabels = (t: TFunction): IChartLabels => {
	const satisfactionLabels = Object.keys(SatisfactionLabels).reduce(
		(res, key) => ({
			...res,
			[key]: t(`satisfaction_levels.${key}`).toUpperCase(),
		}),
		{} as ISatisfactionLabels,
	);
	const influenceLabels = Object.keys(InfluenceLabels).reduce(
		(res, key) => ({
			...res,
			[key]: t(`influence_levels.${key}`).toUpperCase(),
		}),
		{} as IInfluenceLabels,
	);

	return {
		...satisfactionLabels,
		...influenceLabels,
	};
};

const drawLabelsForChartAreas = (
	chartInstance: Chart,
	areasParameters: IAreaParameters,
	chartLabels: IChartLabels,
) => {
	const { ctx, chartArea } = chartInstance;
	if (!ctx) return;
	const { left, right, top, bottom } = chartArea;
	const {
		areaHeight,
		poorAreaWidth,
		okAreaWidth,
		goodAreaWidth,
	} = areasParameters;
	const { poor, ok, good, lower, higher } = chartLabels;

	// Draw poor, ok, good labels for the satisfaction areas
	ctx.font = "20px 'Roboto', sans-serif";
	ctx.fillStyle = '#000';

	const poorHalfWordWidth = ctx.measureText(poor).width / 2;
	const poorXCoordinate = left + poorAreaWidth / 2 - poorHalfWordWidth;
	ctx.fillText(poor, poorXCoordinate, AREA_LABEL_Y_OFFSET);

	const okHalfWordWidth = ctx.measureText(ok).width / 2;
	const okXCoordinate =
		left + poorAreaWidth + okAreaWidth / 2 - okHalfWordWidth;
	ctx.fillText(ok, okXCoordinate, AREA_LABEL_Y_OFFSET);

	const goodHalfWordWidth = ctx.measureText(good).width / 2;
	const goodXCoordinate = right - goodAreaWidth / 2 - goodHalfWordWidth;
	ctx.fillText(good, goodXCoordinate, AREA_LABEL_Y_OFFSET);

	// Draw lower, higher labels for the influence areas
	const higherYCoordinate = areaHeight / 2 + top;
	const lowerYCoordinate = bottom - areaHeight / 2 + WORD_HALF_HEIGHT;
	ctx.fillText(higher, 0, higherYCoordinate);
	ctx.fillText(lower, 0, lowerYCoordinate);
};

export const getDrawPlugin = (
	minAndMaxTicksValues: IXAxesMinMax,
	t: TFunction,
	isBlank: boolean,
) => ({
	beforeDraw(chartInstance: Chart) {
		const { ctx, chartArea } = chartInstance;
		if (!ctx) return;

		const areasParameters = getAreasParameters(chartArea, minAndMaxTicksValues);
		fillChartAreas(chartInstance, areasParameters, isBlank);

		const chartLabels = getChartLabels(t);
		drawLabelsForChartAreas(chartInstance, areasParameters, chartLabels);
	},
});
